import baustein from "baustein";
import globalEvents from "web/script/utils/global-events";
import analytics from "web/script/analytics/analytics";
import browser from "web/script/utils/browser";
import { events as calloutEvents } from "web/script/components/app-header-callout";
import CartSummary from "./cart-summary";

const CART_NUMBER_SELECTOR = ".app-header-cart__cart-number";
const CART_NUMBER_HIDDEN_MODIFIER = "app-header-cart__cart-number--hidden";
const CONTENT_WRAPPER_SELECTOR = ".app-header-cart__content-wrapper";

export default baustein.register("app-header-cart", {
    /**
     * Whether the cart callout is visible or not.
     * This is used to track the visible state of the App Header Callout instance wrapping this
     * component.
     *
     * @see components/app-header/app-header-callout
     * @type {boolean}
     */
    visible: false,

    defaultOptions: {
        itemCount: 0,
    },

    init() {
        this.onItemCountChange = this.onItemCountChange.bind(this);
    },

    setupEvents(add) {
        add("mouseenter", this.onMouseEnter);
        add("closeIntent", "cart-summary", this.hideCartCallout);
        add("oosItemFound", "cart-summary", this.showCartCalloutOOS);
    },

    render() {
        let itemCount = this.options.itemCount;
        let cartNumberSelector = this.el.querySelector(CART_NUMBER_SELECTOR);

        cartNumberSelector.textContent = itemCount;

        if (itemCount > 0 && cartNumberSelector.classList.contains(CART_NUMBER_HIDDEN_MODIFIER)) {
            cartNumberSelector.classList.remove(CART_NUMBER_HIDDEN_MODIFIER);
        }
    },

    onInsert() {
        globalEvents.on("cart.change", this.onItemCountChange);
    },

    onRemove() {
        globalEvents.off("cart.change", this.onItemCountChange);
    },

    onItemCountChange(event, data) {
        this.options.itemCount = this.options.itemCount + data.itemCountDifference;
        this.render();
    },

    /**
     * On mouse enter of the cart button then render the
     * new cart summary.
     */
    onMouseEnter() {
        if (this.isCartCalloutShownPersistently()) {
            // we are waiting for a user response, do nothing
            return;
        }

        this.renderCartSummary();
        analytics.event("header", "show-cart-dropdown");
    },

    /**
     * Shows the cart summary callout persistently (which has to then be dismissed by the user)
     *
     */
    showCartCallout() {
        browser.scrollTop(0);
        this.renderCartSummary();

        this.emit(calloutEvents.TOGGLE);
        this.visible = true;

        this.invoke(this.findComponent("cart-summary"), "showCloseCross");
    },

    /**
     * Shows the cart summary callout persistently. Differs from the above function as there's no
     *  need to render the cart summary.
     */
    showCartCalloutOOS() {
        analytics.event("header", "show-cart-callout-oos", null, true);

        this.emit(calloutEvents.TOGGLE);
        this.visible = true;

        this.invoke(this.findComponent("cart-summary"), "showCloseCross");
    },

    /**
     * Returns true if the cart callout is shown persistently. If it's shown due to
     * the user hovering over the app-header-cart this will false.
     */
    isCartCalloutShownPersistently() {
        return this.visible;
    },

    /**
     * Event handler for the event when the user presses the close cross
     * on the persistent cart callout.
     */
    hideCartCallout() {
        this.emit(calloutEvents.TOGGLE);
        this.visible = false;

        analytics.event("header", "hide-cart-callout");

        // If we don't destroy the component then it will still be shown since we are hovering
        // over it. This is not the nicest solution since it means this component will have to
        // be re-rendered on the next mouseenter but it's much less code and neater than
        // all the state and modifiers that would be need to handle this 'properly'.
        this.invoke(this.findComponent("cart-summary"), "destroy");
    },

    /**
     * Render the cart summary.
     * If it's already there, just update it.
     */
    renderCartSummary() {
        let cartSummary = this.findComponent("cart-summary");

        if (cartSummary) {
            cartSummary.update();
            return;
        }

        cartSummary = new CartSummary();
        cartSummary.appendTo(this.el.querySelector(CONTENT_WRAPPER_SELECTOR));

        this.emit("cartSummaryShown");
    },

    /**
     * Render the cart summary, but for a stock check
     * If it's already there, call checkStock() directly
     */
    checkStock() {
        if (!this.options.itemCount) {
            return;
        }

        let cartSummary = this.findComponent("cart-summary");

        if (cartSummary) {
            cartSummary.checkStock();
            return;
        }

        cartSummary = new CartSummary({ checkStock: true });
        cartSummary.appendTo(this.el.querySelector(CONTENT_WRAPPER_SELECTOR));

        this.emit("cartSummaryShown");
    },
});
